import { useContext, useEffect, useState } from "react";
import { ActionType } from "../State/State";
import { AppContext } from "../State/Context";
import { BasicPopup } from "./BasicPopup";
import { RequestMethod, askAPI } from "../api/api";
import { Hexagen } from "../types";
import { Moon } from "../Misc/Moon";
import { getGenerationPenalty, findNeighboursNumber } from "../World/Hexagon";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Icon } from "../components/Icon";

const maxPromptLength = 100;

export const GeneratePopup: React.FC = () => {
  const { state, dispatch } = useContext(AppContext);
  const [prompt, setPrompt] = useState<string>("");
  const [useSparks, setUseSparks] = useState<boolean>(false);

  const isGeneratingNow = state.generatingNowHexs.some(
    (hex) =>
      hex.x === state.selectedHexCoord?.x && hex.y === state.selectedHexCoord?.y
  );
  const [isRequesting, setIsRequesting] = useState<boolean>(isGeneratingNow);

  useEffect(() => {
    if (
      state.generatingNowHexs.some(
        (hex) =>
          hex.x === state.selectedHexCoord?.x &&
          hex.y === state.selectedHexCoord?.y
      )
    ) {
      setIsRequesting(true);
    } else {
      setIsRequesting(false);
    }
  }, [state.generatingNowHexs, state.selectedHexCoord]);

  useEffect(() => {
    if (state.isHexGeneratePopupOpen) {
      const shouldForceSparks = !canGenerateWithPoints() && (state.player?.sparks || 0) >= 2;
      setUseSparks(shouldForceSparks);
    }
  }, [
    state.isHexGeneratePopupOpen,
    state.booleanMap,
    state.selectedHexCoord,
    state.player?.points,
    state.player?.sparks,
  ]);

  const deleteHex = async () => {
    await askAPI<Hexagen>("/api/hexagen/delete", RequestMethod.POST, {
      x: state.selectedHexCoord?.x,
      y: state.selectedHexCoord?.y,
    });

    window.history.pushState(null, "", "/");
    window.location.reload();
  };

  const handlePromptChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setPrompt(event.target.value.slice(0, maxPromptLength));
  };

  const neighboursNumber = state.selectedHexCoord
    ? findNeighboursNumber(
        Number(state.selectedHexCoord.x),
        Number(state.selectedHexCoord.y),
        state.booleanMap
      )
    : 0;
  const canGenerateHere = neighboursNumber > 0 || (state.player?.points || 0) > 100000;
  const generationCost = Math.abs(getGenerationPenalty(Math.max(1, neighboursNumber)));
  const canGenerateWithPoints = () =>
    canGenerateHere && (state.player?.points || 0) >= generationCost;

  const generateHexagen = async () => {
    if (isRequesting) return;

    setIsRequesting(true);

    if (state.selectedHexCoord) {
      const result = await askAPI<boolean>(
        "/api/hexagen/generate",
        RequestMethod.POST,
        {
          x: state.selectedHexCoord.x,
          y: state.selectedHexCoord.y,
          prompt,
          forceIsometry: false,
          useSparks,
        }
      );

      if (result) {
        dispatch({
          type: ActionType.ADD_GENERATING_NOW_HEX,
          x: state.selectedHexCoord.x,
          y: state.selectedHexCoord.y,
        });
      } else {
        setIsRequesting(false);
      }
    }
  };

  useEffect(() => {
    if (isGeneratingNow) {
      closePopup();
    }
  }, [isGeneratingNow]);

  const closePopup = () => {
    setIsRequesting(false);
    dispatch({ type: ActionType.CLOSE_HEX_GENERATE_POPUP });
  };

  return state.isHexGeneratePopupOpen ? (
    <BasicPopup onClose={closePopup}>
      <div>
        <div className="text-lg mt-4 mb-2">
          Claim hexagon <span className="text-2xl">🎴</span>
        </div>
        <div className="text-sm font-normal opacity-50 mb-6">
          Coordinates: x:{state.selectedHexCoord?.x} y:
          {state.selectedHexCoord?.y}
        </div>
        <div>Create a prompt to generate an object within it.</div>
        <div className="mt-2">
          AI gives the generated hexagon a creativity score. This score and
          upvotes do not add points to your balance.
        </div>
        <div className="mt-6 mb-1 text-sm">Your Prompt:</div>
        <div>
          <textarea
            id="newName"
            value={prompt}
            onChange={handlePromptChange}
            className="w-full p-1 px-2 focus:outline-none h-[70px]"
          ></textarea>
          <div className="text-xs mb-2">
            {maxPromptLength - prompt.length} characters remaining
          </div>
          <div className="mt-2 mb-2 text-xs">
            Generation Cost:
            <span className="font-bold ml-2 mr-1">
              {generationCost}
            </span>
            points or 2 sparks
          </div>

          <label className="flex items-center text-xs mb-4 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mr-2 h-4 w-4 rainbow-checkbox border-0"
              checked={useSparks}
              onChange={(e) => setUseSparks(e.target.checked)}
              disabled={!canGenerateHere || (state.player?.sparks || 0) < 2 || (!canGenerateWithPoints() && (state.player?.sparks || 0) >= 2)}
            />
            Use 🌟 Sparks for generation
          </label>

          {!canGenerateHere ? (
            <div className="text-red-800 mt-4">
              At least one neighboring hexagon is required unless your balance
              is above 100,000 points.
            </div>
          ) : (canGenerateWithPoints() || ((state.player?.sparks || 0) >= 2)) ? (
            isRequesting ? (
              <Moon />
            ) : (
              <button className="rainbow-hover-" onClick={generateHexagen}>
                Generate
              </button>
            )
          ) : (
            <div>
              <div className="text-red-800 mt-4">
                You do not have enough points or Sparks for this generation.
              </div>

              <div className="mt-2">
                Points are granted only once, when you first register.
              </div>
            </div>
          )}

          {state.player?.role === "admin" ? (
            <div
              className="absolute bottom-0 right-0 mb-1 px-2 py-1 ml-1 text-xs cursor-pointer"
              onClick={deleteHex}
            >
              <Icon
                icon={RiDeleteBin6Line}
                className="mr-1 inline text-[14px] translate-y-[-1px]"
              />
            </div>
          ) : null}
        </div>
      </div>
    </BasicPopup>
  ) : null;
};
